/**
 * `@dayvise/design-tokens` — the single source of truth for how Dayvise looks
 * and moves (prompt.md §40, §44).
 *
 * WHY this module shape:
 *
 *  - The tokens are **platform-neutral data**. Web, Tauri desktop and Android
 *    must share semantic *names* (`accent`, `space.lg`, `motion.fast`) even
 *    though CSS custom properties and Compose `MaterialTheme` are unrelated
 *    technologies (prompt.md §44). Anything CSS-specific lives behind an
 *    explicitly named adapter (`cssVariables`, `cssBoxShadow`).
 *  - Consumers never hardcode a colour, size or duration. They either read a
 *    typed token or reference a `--dayvise-*` custom property, so re-theming
 *    (or adding a high-contrast scheme) is a change in this package alone.
 *  - `resolveTokens(scheme)` exists for programmatic consumers, while
 *    `cssVariables(scheme)` exists for the stylesheet/`style` attribute path.
 *    Both are derived from the same constants, so they cannot drift.
 */

import { type ColorRole, type ColorScheme, COLORS, COLOR_ROLES } from "./colors.ts";
import {
  type MotionDurationToken,
  type MotionEasingName,
  MOTION_DURATIONS,
  MOTION_EASINGS,
  MOTION_SPRINGS,
} from "./motion.ts";
import {
  ELEVATIONS,
  RADII,
  SPACING,
  Z_INDEX,
  cssBoxShadow,
  type ElevationName,
  type RadiusToken,
  type SpacingToken,
  type ZIndexToken,
} from "./spacing.ts";
import {
  FONT_FAMILIES,
  FONT_SIZES,
  FONT_WEIGHTS,
  LETTER_SPACINGS,
  LINE_HEIGHTS,
  TEXT_STYLES,
} from "./typography.ts";

export * from "./colors.ts";
export * from "./typography.ts";
export * from "./spacing.ts";
export * from "./motion.ts";

/** Prefix every custom property carries, so tokens are unambiguous in a page. */
export const CSS_VARIABLE_PREFIX = "--dayvise-";

/** Lower camel to kebab, preserving digit-leading steps such as `2xl`. */
function kebab(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

/** Build a custom-property name from a group and one or more token segments. */
export function cssVariableName(group: string, ...segments: readonly string[]): string {
  return `${CSS_VARIABLE_PREFIX}${[group, ...segments].map(kebab).join("-")}`;
}

/**
 * Every custom property for a scheme, as a plain record. React components spread
 * this into a `style` object; a build step can also print it as a stylesheet.
 */
export function cssVariableDeclarations(scheme: ColorScheme): Readonly<Record<string, string>> {
  const declarations: Record<string, string> = {};

  for (const role of COLOR_ROLES) {
    declarations[cssVariableName("color", role)] = COLORS[role][scheme];
  }
  for (const [name, value] of Object.entries(FONT_FAMILIES)) {
    declarations[cssVariableName("font", "family", name)] = value;
  }
  for (const [name, value] of Object.entries(FONT_SIZES)) {
    declarations[cssVariableName("font", "size", name)] = value;
  }
  for (const [name, value] of Object.entries(FONT_WEIGHTS)) {
    declarations[cssVariableName("font", "weight", name)] = String(value);
  }
  for (const [name, value] of Object.entries(LINE_HEIGHTS)) {
    declarations[cssVariableName("line", "height", name)] = String(value);
  }
  for (const [name, value] of Object.entries(LETTER_SPACINGS)) {
    declarations[cssVariableName("letter", "spacing", name)] = value;
  }
  for (const [name, value] of Object.entries(SPACING)) {
    declarations[cssVariableName("space", name)] = `${value}px`;
  }
  for (const [name, value] of Object.entries(RADII)) {
    declarations[cssVariableName("radius", name)] = name === "pill" || name === "full" ? "9999px" : `${value}px`;
  }
  for (const [name, token] of Object.entries(ELEVATIONS)) {
    declarations[cssVariableName("elevation", name, "level")] = String(token.level);
    declarations[cssVariableName("elevation", name, "offset", "y")] = `${token.offsetY}px`;
    declarations[cssVariableName("elevation", name, "blur")] = `${token.blur}px`;
    declarations[cssVariableName("elevation", name, "spread")] = `${token.spread}px`;
    declarations[cssVariableName("elevation", name, "opacity")] =
      String(scheme === "dark" ? token.darkOpacity : token.lightOpacity);
    declarations[cssVariableName("elevation", name, "shadow")] = cssBoxShadow(name as ElevationName, scheme);
  }
  for (const [name, duration] of Object.entries(MOTION_DURATIONS)) {
    declarations[cssVariableName("motion", "duration", name)] = `${duration}ms`;
  }
  for (const [name, easing] of Object.entries(MOTION_EASINGS)) {
    declarations[cssVariableName("motion", "easing", name)] = `cubic-bezier(${easing.x1}, ${easing.y1}, ${easing.x2}, ${easing.y2})`;
  }
  for (const [name, spring] of Object.entries(MOTION_SPRINGS)) {
    declarations[cssVariableName("motion", "spring", name, "stiffness")] = String(spring.stiffness);
    declarations[cssVariableName("motion", "spring", name, "damping", "ratio")] = String(spring.dampingRatio);
  }
  for (const [name, value] of Object.entries(Z_INDEX)) {
    declarations[cssVariableName("z", name)] = String(value);
  }

  return declarations;
}

/**
 * A ready-to-inject custom-property block, e.g. `:root { --dayvise-color-accent: #3b5bfd; ... }`.
 * Keys are emitted in the deterministic order produced above so diffs stay small.
 */
export function cssVariables(scheme: ColorScheme, selector = ":root"): string {
  const declarations = cssVariableDeclarations(scheme);
  const body = Object.entries(declarations)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join("\n");
  return `${selector} {\n${body}\n}`;
}

/** The canonical token names every platform is expected to mirror. */
export function semanticTokenNames(): readonly string[] {
  return [...new Set([...Object.keys(cssVariableDeclarations("light")), ...Object.keys(cssVariableDeclarations("dark"))])].sort();
}

/** Structured view of the tokens for programmatic (non-CSS) consumers. */
export function resolveTokens(scheme: ColorScheme) {
  const colors = Object.fromEntries(
    COLOR_ROLES.map((role) => [role, COLORS[role][scheme]]),
  ) as Readonly<Record<ColorRole, string>>;

  return {
    scheme,
    colors,
    typography: {
      families: FONT_FAMILIES,
      sizes: FONT_SIZES,
      weights: FONT_WEIGHTS,
      lineHeights: LINE_HEIGHTS,
      letterSpacings: LETTER_SPACINGS,
      styles: TEXT_STYLES,
    },
    spacing: SPACING,
    radii: RADII,
    elevation: ELEVATIONS,
    motion: {
      durationsMs: MOTION_DURATIONS,
      easings: MOTION_EASINGS,
      springs: MOTION_SPRINGS,
    },
    zIndex: Z_INDEX,
  } as const;
}

export type DesignTokens = ReturnType<typeof resolveTokens>;

/** Convenience: a custom property reference, e.g. `var(--dayvise-color-accent)`. */
export function token(group: string, ...segments: readonly string[]): string {
  return `var(${cssVariableName(group, ...segments)})`;
}

/** Re-exported type aliases, so consumers import them from one place. */
export type {
  ColorRole,
  ColorScheme,
  MotionDurationToken,
  MotionEasingName,
  RadiusToken,
  SpacingToken,
  ZIndexToken,
};
